import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  MenuItem,
  Button,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Menu,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  TablePagination,
  Autocomplete,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  ViewColumn as ColumnsIcon,
  Search as SearchIcon,
  Inventory2,
  CompareArrows,
  TrendingUp,
  TrendingDown,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { StockCount } from '../../types/stockCounts.types';
import { stockCountsService } from '../../services/stockCounts.service';
import { Warehouse } from '../../types/warehouses.types';
import { warehousesService } from '../../services/warehouses.service';
import { Branch } from '../../types/branches.types';
import { branchesService } from '../../services/branches.service';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';

const translations = {
  en: {
    title: 'Stock Counts Report',
    titleAr: 'تقرير جرد المخزون',
    warehouse: 'Warehouse',
    branch: 'Branch',
    allWarehouses: 'All Warehouses',
    allBranches: 'All Branches',
    allStatuses: 'All Statuses',
    status: 'Status',
    dateFrom: 'From Date',
    dateTo: 'To Date',
    search: 'Search...',
    today: 'Today',
    thisWeek: 'This Week',
    thisMonth: 'This Month',
    lastMonth: 'Last Month',
    thisYear: 'This Year',
    detailedView: 'Detailed Counts',
    summaryByWarehouse: 'Summary by Warehouse',
    summaryByBranch: 'Summary by Branch',
    summaryByItem: 'Summary by Item',
    varianceAnalysis: 'Variance Analysis',
    countNumber: 'Count #',
    countDate: 'Date',
    warehouseName: 'Warehouse',
    branchName: 'Branch',
    itemCode: 'Item Code',
    itemName: 'Item',
    unitName: 'Unit',
    systemQty: 'System Qty',
    physicalQty: 'Physical Qty',
    variance: 'Variance',
    varianceValue: 'Variance Value',
    unitCost: 'Unit Cost',
    notes: 'Notes',
    totalCounts: 'Total Counts',
    itemsCounted: 'Items Counted',
    totalVarianceValue: 'Total Variance',
    variancePct: 'Variance %',
    inProgress: 'In Progress',
    completed: 'Completed',
    approved: 'Approved',
    posted: 'Posted',
    stats: 'Statistics',
    countsCount: 'Total Counts',
    refresh: 'Refresh',
    exportExcel: 'Export Excel',
    exportPdf: 'Export PDF',
    print: 'Print',
    columns: 'Columns',
    filters: 'Filters',
    noData: 'No stock counts found',
    loading: 'Loading...',
    kwd: 'KWD',
    positive: 'Surplus',
    negative: 'Shortage',
  },
  ar: {
    title: 'تقرير جرد المخزون',
    titleAr: 'تقرير جرد المخزون',
    warehouse: 'المخزن',
    branch: 'الفرع',
    allWarehouses: 'جميع المخازن',
    allBranches: 'جميع الفروع',
    allStatuses: 'جميع الحالات',
    status: 'الحالة',
    dateFrom: 'من تاريخ',
    dateTo: 'إلى تاريخ',
    search: 'بحث...',
    today: 'اليوم',
    thisWeek: 'هذا الأسبوع',
    thisMonth: 'هذا الشهر',
    lastMonth: 'الشهر الماضي',
    thisYear: 'هذه السنة',
    detailedView: 'الجرد التفصيلي',
    summaryByWarehouse: 'ملخص حسب المخزن',
    summaryByBranch: 'ملخص حسب الفرع',
    summaryByItem: 'ملخص حسب الصنف',
    varianceAnalysis: 'تحليل الفروقات',
    countNumber: 'رقم الجرد',
    countDate: 'التاريخ',
    warehouseName: 'المخزن',
    branchName: 'الفرع',
    itemCode: 'كود الصنف',
    itemName: 'الصنف',
    unitName: 'الوحدة',
    systemQty: 'كمية النظام',
    physicalQty: 'الكمية الفعلية',
    variance: 'الفرق',
    varianceValue: 'قيمة الفرق',
    unitCost: 'سعر الوحدة',
    notes: 'ملاحظات',
    totalCounts: 'إجمالي الجرد',
    itemsCounted: 'الأصناف المجردة',
    totalVarianceValue: 'إجمالي الفروقات',
    variancePct: 'نسبة الفرق %',
    inProgress: 'قيد التنفيذ',
    completed: 'مكتمل',
    approved: 'معتمد',
    posted: 'مرحّل',
    stats: 'الإحصائيات',
    countsCount: 'عدد الجرد',
    refresh: 'تحديث',
    exportExcel: 'تصدير Excel',
    exportPdf: 'تصدير PDF',
    print: 'طباعة',
    columns: 'الأعمدة',
    filters: 'الفلاتر',
    noData: 'لا يوجد جرد مخزون',
    loading: 'جاري التحميل...',
    kwd: 'د.ك',
    positive: 'فائض',
    negative: 'عجز',
  },
};

interface DetailedCount {
  countNumber: string;
  countDate: string;
  warehouseId?: number;
  warehouseName: string;
  branchId?: number;
  branchName: string;
  itemCode: string;
  itemName: string;
  itemNameAr: string;
  unitName: string;
  unitNameAr: string;
  systemQuantity: number;
  physicalQuantity?: number;
  variance?: number;
  varianceValue?: number;
  unitCost: number;
  status: string;
}

interface LocationSummary {
  locationId?: number;
  locationName: string;
  locationNameAr: string;
  countsCount: number;
  totalItems: number;
  totalVarianceValue: number;
  positiveVariance: number;
  negativeVariance: number;
}

interface ItemSummary {
  itemId: number;
  itemCode: string;
  itemName: string;
  itemNameAr: string;
  countsCount: number;
  totalSystemQty: number;
  totalPhysicalQty: number;
  totalVariance: number;
  totalVarianceValue: number;
}

interface VarianceItem {
  itemCode: string;
  itemName: string;
  itemNameAr: string;
  systemQuantity: number;
  physicalQuantity: number;
  variance: number;
  varianceValue: number;
  variancePct: number;
}

const StockCountsReport: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();

  const [counts, setCounts] = useState<StockCount[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  
  const [warehouseFilter, setWarehouseFilter] = useState<number | ''>('');
  const [branchFilter, setBranchFilter] = useState<number | ''>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [datePreset, setDatePreset] = useState<string>('');
  const [detailPage, setDetailPage] = useState(0);
  const [detailRowsPerPage, setDetailRowsPerPage] = useState(25);

  const [columnsAnchor, setColumnsAnchor] = useState<null | HTMLElement>(null);
  const [visibleColumns, setVisibleColumns] = useState({
    countNumber: true,
    date: true,
    location: true,
    item: true,
    systemQty: true,
    physicalQty: true,
    variance: true,
    value: true,
  });

  useEffect(() => {
    loadWarehouses();
    loadBranches();
    loadCompanyInfo();
  }, []);

  useEffect(() => {
    loadData();
    setDetailPage(0);
  }, [warehouseFilter, branchFilter, statusFilter, dateFromFilter, dateToFilter]);

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  // ✅ تعديل: فلترة المخازن من JWT
  const loadWarehouses = async () => {
    try {
      const data = await warehousesService.getAll();

      const token = localStorage.getItem('token');
      let userBranchIds: number[] = [];
      let userWarehouseIds: number[] = [];

      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const primaryBranch = payload.BranchId ? [Number(payload.BranchId)] : [];
          const allowedBranches = payload.AllowedBranchId
            ? (Array.isArray(payload.AllowedBranchId) ? payload.AllowedBranchId.map(Number) : [Number(payload.AllowedBranchId)])
            : [];
          userBranchIds = Array.from(new Set([...primaryBranch, ...allowedBranches]));

          const keeperClaims = payload.KeeperWarehouseId;
          userWarehouseIds = Array.isArray(keeperClaims) ? keeperClaims.map(Number) : keeperClaims ? [Number(keeperClaims)] : [];
        } catch (e) { console.error('Failed to parse token:', e); }
      }

      const filteredWarehouses = userWarehouseIds.length > 0
        ? data.filter((w: Warehouse) => userWarehouseIds.includes(w.warehouseId))
        : userBranchIds.length === 0
          ? data
          : data.filter((w: Warehouse) => w.branchId && userBranchIds.includes(w.branchId));

      setWarehouses(filteredWarehouses);
    } catch (error) {
      console.error('Failed to load warehouses:', error);
    }
  };

  // ✅ تعديل: فلترة الفروع من JWT
  const loadBranches = async () => {
    try {
      const data = await branchesService.getAll();

      const token = localStorage.getItem('token');
      let userBranchIds: number[] = [];
      let userWarehouseIds: number[] = [];

      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const primaryBranch = payload.BranchId ? [Number(payload.BranchId)] : [];
          const allowedBranches = payload.AllowedBranchId
            ? (Array.isArray(payload.AllowedBranchId) ? payload.AllowedBranchId.map(Number) : [Number(payload.AllowedBranchId)])
            : [];
          userBranchIds = Array.from(new Set([...primaryBranch, ...allowedBranches]));

          const keeperClaims = payload.KeeperWarehouseId;
          userWarehouseIds = Array.isArray(keeperClaims) ? keeperClaims.map(Number) : keeperClaims ? [Number(keeperClaims)] : [];
        } catch (e) { console.error('Failed to parse token:', e); }
      }

      const filteredBranches = userWarehouseIds.length > 0
        ? []
        : userBranchIds.length === 0
          ? data
          : data.filter((b: Branch) => userBranchIds.includes(b.branchId));

      setBranches(filteredBranches);
    } catch (error) {
      console.error('Failed to load branches:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await stockCountsService.getAll(
        statusFilter || undefined,
        warehouseFilter || undefined,
        branchFilter || undefined,
        dateFromFilter || undefined,
        dateToFilter || undefined
      );
      setCounts(data);
    } catch (error) {
      console.error('Failed to load stock counts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDatePreset = (preset: string) => {
    setDatePreset(preset);
    const today = new Date();
    let fromDate = '';
    let toDate = today.toISOString().split('T')[0];

    switch(preset) {
      case 'today':
        fromDate = toDate;
        break;
      case 'thisWeek':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        fromDate = weekStart.toISOString().split('T')[0];
        break;
      case 'thisMonth':
        fromDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        break;
      case 'lastMonth':
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        fromDate = lastMonth.toISOString().split('T')[0];
        toDate = new Date(today.getFullYear(), today.getMonth(), 0).toISOString().split('T')[0];
        break;
      case 'thisYear':
        fromDate = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
        break;
    }

    setDateFromFilter(fromDate);
    setDateToFilter(toDate);
  };

  const getDetailedData = (): DetailedCount[] => {
    const detailed: DetailedCount[] = [];
    
    counts.forEach(count => {
      count.details.forEach(detail => {
        detailed.push({
          countNumber: count.countNumber,
          countDate: count.countDate,
          warehouseId: count.warehouseId,
          warehouseName: isRTL ? (count.warehouseNameAr || count.warehouseName || '-') : (count.warehouseName || '-'),
          branchId: count.branchId,
          branchName: isRTL ? (count.branchNameAr || count.branchName || '-') : (count.branchName || '-'),
          itemCode: detail.itemCode,
          itemName: detail.itemName,
          itemNameAr: detail.itemNameAr,
          unitName: detail.unitName,
          unitNameAr: detail.unitNameAr,
          systemQuantity: detail.systemQuantity,
          physicalQuantity: detail.physicalQuantity,
          variance: detail.variance,
          varianceValue: detail.varianceValue,
          unitCost: detail.unitCost,
          status: count.status || 'InProgress',
        });
      });
    });

    return detailed;
  };

  const getFilteredData = () => {
    let filtered = getDetailedData();
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.countNumber.toLowerCase().includes(query) ||
        r.warehouseName.toLowerCase().includes(query) ||
        r.branchName.toLowerCase().includes(query) ||
        r.itemName.toLowerCase().includes(query) ||
        r.itemCode.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  };

  const getSummaryByWarehouse = (): LocationSummary[] => {
    const summary = new Map<number, LocationSummary>();
    
    counts.forEach(count => {
      if (count.warehouseId) {
        if (!summary.has(count.warehouseId)) {
          summary.set(count.warehouseId, {
            locationId: count.warehouseId,
            locationName: count.warehouseName || '-',
            locationNameAr: count.warehouseNameAr || '-',
            countsCount: 0,
            totalItems: 0,
            totalVarianceValue: 0,
            positiveVariance: 0,
            negativeVariance: 0,
          });
        }
        
        const loc = summary.get(count.warehouseId)!;
        loc.countsCount++;
        count.details.forEach(d => {
          loc.totalItems++;
          const variance = d.varianceValue || 0;
          loc.totalVarianceValue += variance;
          if (variance > 0) loc.positiveVariance += variance;
          if (variance < 0) loc.negativeVariance += variance;
        });
      }
    });
    
    return Array.from(summary.values()).sort((a, b) => Math.abs(b.totalVarianceValue) - Math.abs(a.totalVarianceValue));
  };

  const getSummaryByBranch = (): LocationSummary[] => {
    const summary = new Map<number, LocationSummary>();
    
    counts.forEach(count => {
      if (count.branchId) {
        if (!summary.has(count.branchId)) {
          summary.set(count.branchId, {
            locationId: count.branchId,
            locationName: count.branchName || '-',
            locationNameAr: count.branchNameAr || '-',
            countsCount: 0,
            totalItems: 0,
            totalVarianceValue: 0,
            positiveVariance: 0,
            negativeVariance: 0,
          });
        }
        
        const loc = summary.get(count.branchId)!;
        loc.countsCount++;
        count.details.forEach(d => {
          loc.totalItems++;
          const variance = d.varianceValue || 0;
          loc.totalVarianceValue += variance;
          if (variance > 0) loc.positiveVariance += variance;
          if (variance < 0) loc.negativeVariance += variance;
        });
      }
    });
    
    return Array.from(summary.values()).sort((a, b) => Math.abs(b.totalVarianceValue) - Math.abs(a.totalVarianceValue));
  };

  const getSummaryByItem = (): ItemSummary[] => {
    const summary = new Map<number, ItemSummary>();
    
    counts.forEach(count => {
      count.details.forEach(detail => {
        if (!summary.has(detail.itemId)) {
          summary.set(detail.itemId, {
            itemId: detail.itemId,
            itemCode: detail.itemCode,
            itemName: detail.itemName,
            itemNameAr: detail.itemNameAr,
            countsCount: 0,
            totalSystemQty: 0,
            totalPhysicalQty: 0,
            totalVariance: 0,
            totalVarianceValue: 0,
          });
        }
        
        const item = summary.get(detail.itemId)!;
        item.countsCount++;
        item.totalSystemQty += detail.systemQuantity;
        item.totalPhysicalQty += (detail.physicalQuantity || 0);
        item.totalVariance += (detail.variance || 0);
        item.totalVarianceValue += (detail.varianceValue || 0);
      });
    });
    
    return Array.from(summary.values()).sort((a, b) => Math.abs(b.totalVarianceValue) - Math.abs(a.totalVarianceValue));
  };

  const getVarianceAnalysis = (): VarianceItem[] => {
    const items: VarianceItem[] = [];
    
    counts.forEach(count => {
      count.details.forEach(detail => {
        if (detail.variance && detail.variance !== 0) {
          items.push({
            itemCode: detail.itemCode,
            itemName: detail.itemName,
            itemNameAr: detail.itemNameAr,
            systemQuantity: detail.systemQuantity,
            physicalQuantity: detail.physicalQuantity || 0,
            variance: detail.variance,
            varianceValue: detail.varianceValue || 0,
            variancePct: detail.systemQuantity > 0 ? (detail.variance / detail.systemQuantity) * 100 : 0,
          });
        }
      });
    });
    
    return items.sort((a, b) => Math.abs(b.varianceValue) - Math.abs(a.varianceValue));
  };

  const getStatistics = () => {
    const detailed = getDetailedData();
    const uniqueItems = new Set(detailed.map(d => d.itemCode)).size;
    const totalVariance = detailed.reduce((sum, d) => sum + (d.varianceValue || 0), 0);
    const totalSystemValue = detailed.reduce((sum, d) => sum + (d.systemQuantity * d.unitCost), 0);
    const variancePct = totalSystemValue > 0 ? (totalVariance / totalSystemValue) * 100 : 0;
    
    return {
      countsCount: counts.length,
      itemsCounted: uniqueItems,
      totalVarianceValue: totalVariance,
      variancePct: variancePct,
    };
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');
  };

  const formatCurrency = (amount: number) => {
    return amount.toFixed(3);
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'Posted': return 'success';
      case 'Approved': return 'info';
      case 'Completed': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch(status) {
      case 'Posted': return t.posted;
      case 'Approved': return t.approved;
      case 'Completed': return t.completed;
      default: return t.inProgress;
    }
  };

  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t.title);

      if (companyInfo?.logoBase64) {
        try {
          const logoImage = workbook.addImage({
            base64: companyInfo.logoBase64,
            extension: 'png',
          });
          worksheet.addImage(logoImage, {
            tl: { col: 0, row: 0 },
            ext: { width: 100, height: 80 }
          });
        } catch (err) {
          console.warn('Could not add logo:', err);
        }
      }

      worksheet.mergeCells('C1:K1');
      const companyCell = worksheet.getCell('C1');
      companyCell.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
      companyCell.font = { size: 20, bold: true, color: { argb: 'FF1565C0' } };
      companyCell.alignment = { horizontal: 'center', vertical: 'middle' };

      worksheet.mergeCells('C2:K2');
      const addressCell = worksheet.getCell('C2');
      addressCell.value = companyInfo?.address || '';
      addressCell.font = { size: 10 };
      addressCell.alignment = { horizontal: 'center' };

      worksheet.mergeCells('C3:K3');
      const contactCell = worksheet.getCell('C3');
      contactCell.value = `${t.kwd} | ${companyInfo?.phone || ''} | ${companyInfo?.email || ''}`;
      contactCell.font = { size: 9 };
      contactCell.alignment = { horizontal: 'center' };

      worksheet.getRow(4).height = 10;

      worksheet.mergeCells('A5:M5');
      const titleCell = worksheet.getCell('A5');
      titleCell.value = isRTL ? t.titleAr : t.title;
      titleCell.font = { size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF1565C0' }
      };

      const tabNames = [t.detailedView, t.summaryByWarehouse, t.summaryByBranch, t.summaryByItem, t.varianceAnalysis];
      worksheet.mergeCells('A6:M6');
      const tabCell = worksheet.getCell('A6');
      tabCell.value = `📊 ${tabNames[currentTab]}`;
      tabCell.font = { size: 12, bold: true, color: { argb: 'FF1565C0' } };
      tabCell.alignment = { horizontal: 'center' };

      worksheet.getRow(7).height = 10;

      if (currentTab === 0) {
        const data = getFilteredData();
        
        const headers = [
          t.countNumber, t.countDate, t.warehouseName, t.branchName,
          t.itemCode, t.itemName, t.unitName,
          t.systemQty, t.physicalQty, t.variance, t.varianceValue, t.status
        ];
        
        const headerRow = worksheet.getRow(8);
        headers.forEach((header, i) => {
          const cell = headerRow.getCell(i + 1);
          cell.value = header;
          cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF1565C0' }
          };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        });
        
        let currentRow = 9;
        data.forEach((item, index) => {
          const row = worksheet.getRow(currentRow);
          row.getCell(1).value = item.countNumber;
          row.getCell(2).value = formatDate(item.countDate);
          row.getCell(3).value = item.warehouseName;
          row.getCell(4).value = item.branchName;
          row.getCell(5).value = item.itemCode;
          row.getCell(6).value = isRTL ? item.itemNameAr : item.itemName;
          row.getCell(7).value = isRTL ? item.unitNameAr : item.unitName;
          row.getCell(8).value = item.systemQuantity;
          row.getCell(9).value = item.physicalQuantity || 0;
          row.getCell(10).value = item.variance || 0;
          row.getCell(11).value = item.varianceValue || 0;
          row.getCell(12).value = getStatusLabel(item.status);
          
          if (index % 2 === 0) {
            row.eachCell(cell => {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFF5F5F5' }
              };
            });
          }
          
          currentRow++;
        });
        
        worksheet.columns = [
          { width: 16 }, { width: 14 }, { width: 20 }, { width: 18 },
          { width: 14 }, { width: 30 }, { width: 12 },
          { width: 12 }, { width: 12 }, { width: 12 }, { width: 14 }, { width: 12 }
        ];
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `StockCounts_${new Date().getTime()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(isRTL ? 'تم التصدير بنجاح ✅' : 'Exported successfully ✅');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(isRTL ? 'فشل التصدير ❌' : 'Export failed ❌');
    }
  };

  const exportToPdf = async () => {
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.width;

      doc.setFillColor(245, 250, 255);
      doc.rect(0, 0, pageWidth, 45, 'F');

      if (companyInfo?.logoBase64) {
        try {
          doc.addImage(companyInfo.logoBase64, 'PNG', 12, 8, 30, 25);
        } catch (err) {
          console.warn('Could not add logo:', err);
        }
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(21, 101, 192);
      doc.text(isRTL ? (companyInfo?.companyNameAr || '') : (companyInfo?.companyName || ''), pageWidth / 2, 15, { align: 'center' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(companyInfo?.address || '', pageWidth / 2, 22, { align: 'center' });
      doc.text(`${companyInfo?.phone || ''} | ${companyInfo?.email || ''}`, pageWidth / 2, 27, { align: 'center' });

      doc.setFillColor(21, 101, 192);
      doc.roundedRect(pageWidth / 2 - 60, 32, 120, 10, 3, 3, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.text(isRTL ? t.titleAr : t.title, pageWidth / 2, 38.5, { align: 'center' });

      doc.setFontSize(9);
      doc.setTextColor(21, 101, 192);
      doc.text(new Date().toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB'), pageWidth - 15, 38.5, { align: 'right' });

      const tableHeaders = [
        t.countDate,
        t.countNumber,
        t.itemName,
        t.systemQty,
        t.physicalQty,
        t.variance,
        t.varianceValue,
      ];

      const tableData: any[] = [];
      const data = getFilteredData();

      data.forEach((item) => {
        tableData.push([
          formatDate(item.countDate),
          item.countNumber,
          (isRTL ? item.itemNameAr : item.itemName).substring(0, 20),
          formatCurrency(item.systemQuantity),
          formatCurrency(item.physicalQuantity || 0),
          formatCurrency(item.variance || 0),
          formatCurrency(item.varianceValue || 0),
        ]);
      });

      const stats = getStatistics();
      tableData.push([
        {
          content: 'TOTAL',
          colSpan: 6,
          styles: {
            fillColor: [13, 71, 161],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 11,
            halign: 'center'
          }
        },
        {
          content: formatCurrency(stats.totalVarianceValue),
          styles: { fillColor: [13, 71, 161], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 11, halign: 'center' }
        }
      ]);

      autoTable(doc, {
        head: [tableHeaders],
        body: tableData,
        startY: 50,
        theme: 'striped',
        headStyles: {
          fillColor: [21, 101, 192],
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: 'bold',
          halign: 'center'
        },
        bodyStyles: {
          fontSize: 8,
          textColor: [50, 50, 50]
        },
        alternateRowStyles: {
          fillColor: [248, 250, 251]
        },
      });

      doc.save(`StockCounts_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(isRTL ? 'تم التصدير بنجاح ✅' : 'Exported successfully ✅');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error(isRTL ? 'فشل التصدير ❌' : 'Export failed ❌');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const stats = getStatistics();

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ 
          background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontWeight: 'bold'
        }}>
          {t.title}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }} className="no-print">
          <Tooltip title={t.refresh}>
            <IconButton onClick={loadData} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t.columns}>
            <IconButton onClick={(e) => setColumnsAnchor(e.currentTarget)} color="primary">
              <ColumnsIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<ExcelIcon />} onClick={exportToExcel}
            sx={{ background: 'linear-gradient(45deg, #4CAF50 30%, #66BB6A 90%)', color: 'white' }}>
            {t.exportExcel}
          </Button>
          <Button variant="contained" startIcon={<PdfIcon />} onClick={exportToPdf}
            sx={{ background: 'linear-gradient(45deg, #f44336 30%, #e57373 90%)', color: 'white' }}>
            {t.exportPdf}
          </Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}
            sx={{ background: 'linear-gradient(45deg, #FF9800 30%, #FFB74D 90%)', color: 'white' }}>
            {t.print}
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }} className="no-print">
        <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.countsCount}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{stats.countsCount}</Typography>
              </Box>
              <Inventory2 sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.itemsCounted}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{stats.itemsCounted}</Typography>
              </Box>
              <CompareArrows sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ background: stats.totalVarianceValue >= 0 ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' : 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.totalVarianceValue}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{formatCurrency(stats.totalVarianceValue)}</Typography>
              </Box>
              {stats.totalVarianceValue >= 0 ? <TrendingUp sx={{ fontSize: 48, opacity: 0.3 }} /> : <TrendingDown sx={{ fontSize: 48, opacity: 0.3 }} />}
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.variancePct}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{stats.variancePct.toFixed(2)}%</Typography>
              </Box>
              <CompareArrows sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }} className="no-print">
        <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>{t.filters}</Typography>
        
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {['today', 'thisWeek', 'thisMonth', 'lastMonth', 'thisYear'].map((preset) => (
            <Chip key={preset} label={t[preset as keyof typeof t]} onClick={() => handleDatePreset(preset)}
              color={datePreset === preset ? 'primary' : 'default'} variant={datePreset === preset ? 'filled' : 'outlined'} />
          ))}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2 }}>
          <Autocomplete
            size="small"
            options={[{ id: '' as number | '', label: t.allWarehouses }, ...warehouses.map(wh => ({ id: wh.warehouseId as number | '', label: isRTL ? wh.warehouseNameAr || wh.warehouseName : wh.warehouseName }))]}
            getOptionLabel={(o) => o.label || ''}
            value={[{ id: '' as number | '', label: t.allWarehouses }, ...warehouses.map(wh => ({ id: wh.warehouseId as number | '', label: isRTL ? wh.warehouseNameAr || wh.warehouseName : wh.warehouseName }))].find(o => o.id === warehouseFilter) || { id: '' as number | '', label: t.allWarehouses }}
            onChange={(_, v) => setWarehouseFilter(v?.id || '')}
            renderInput={(params) => <TextField {...params} label={t.warehouse} />}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            fullWidth
          />

          <Autocomplete
            size="small"
            options={[{ id: '' as number | '', label: t.allBranches }, ...branches.map(br => ({ id: br.branchId as number | '', label: isRTL ? br.branchNameAr || br.branchName : br.branchName }))]}
            getOptionLabel={(o) => o.label || ''}
            value={[{ id: '' as number | '', label: t.allBranches }, ...branches.map(br => ({ id: br.branchId as number | '', label: isRTL ? br.branchNameAr || br.branchName : br.branchName }))].find(o => o.id === branchFilter) || { id: '' as number | '', label: t.allBranches }}
            onChange={(_, v) => setBranchFilter(v?.id || '')}
            renderInput={(params) => <TextField {...params} label={t.branch} />}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            fullWidth
          />

          <TextField select fullWidth size="small" label={t.status} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">{t.allStatuses}</MenuItem>
            <MenuItem value="InProgress">{t.inProgress}</MenuItem>
            <MenuItem value="Completed">{t.completed}</MenuItem>
            <MenuItem value="Approved">{t.approved}</MenuItem>
            <MenuItem value="Posted">{t.posted}</MenuItem>
          </TextField>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.dateFrom}
              value={dateFromFilter ? new Date(dateFromFilter) : null}
              onChange={(date) => {
                const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setDateFromFilter(newValue);
                if (dateToFilter && newValue && dateToFilter < newValue) setDateToFilter('');
              }}
              format="dd/MM/yyyy"
              views={['year', 'month', 'day']}
              openTo="day"
              minDate={minDate}
              maxDate={dateToFilter ? new Date(dateToFilter) : maxDate}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </LocalizationProvider>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.dateTo}
              value={dateToFilter ? new Date(dateToFilter) : null}
              onChange={(date) => {
                const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setDateToFilter(newValue);
                if (dateFromFilter && newValue && dateFromFilter > newValue) setDateFromFilter('');
              }}
              format="dd/MM/yyyy"
              views={['year', 'month', 'day']}
              openTo="day"
              minDate={dateFromFilter ? new Date(dateFromFilter) : minDate}
              maxDate={maxDate}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </LocalizationProvider>
        </Box>

        <TextField fullWidth size="small" placeholder={t.search} value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setDetailPage(0); }} sx={{ mt: 2 }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }} />
      </Paper>

      <Paper sx={{ mb: 2 }} className="no-print">
        <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)} variant="scrollable" scrollButtons="auto">
          <Tab label={t.detailedView} />
          <Tab label={t.summaryByWarehouse} />
          <Tab label={t.summaryByBranch} />
          <Tab label={t.summaryByItem} />
          <Tab label={t.varianceAnalysis} />
        </Tabs>
      </Paper>

      <Box className="print-only">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, pb: 1, borderBottom: '2px solid #1565C0' }}>
          {companyInfo?.logoBase64 && (
            <Box><img src={companyInfo.logoBase64} alt="Logo" style={{ height: '40px', maxWidth: '100px' }} /></Box>
          )}
          <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography sx={{ fontSize: '20px', fontWeight: 'bold', color: '#1565C0', mb: 0.3 }}>
              {isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName}
            </Typography>
            <Typography sx={{ fontSize: '9px', color: '#546E7A' }}>{companyInfo?.address || ''} | 📞 {companyInfo?.phone || ''}</Typography>
          </Box>
          <Box sx={{ width: '100px' }}></Box>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 1, py: 0.5, background: 'linear-gradient(90deg, #1565C0 0%, #1976D2 100%)', color: 'white' }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 'bold' }}>{isRTL ? t.titleAr : t.title} - {[t.detailedView, t.summaryByWarehouse, t.summaryByBranch, t.summaryByItem, t.varianceAnalysis][currentTab]}</Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, fontSize: '8px', color: '#546E7A' }}>
          <Typography sx={{ fontSize: '8px' }}>
            {new Date().toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB', { year: 'numeric', month: 'short', day: 'numeric' })}
          </Typography>
          <Typography sx={{ fontSize: '8px' }}>{new Date().toLocaleTimeString(isRTL ? 'ar-KW' : 'en-GB')}</Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
          {currentTab === 0 && (
            <>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)' }}>
                  {visibleColumns.countNumber && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.countNumber}</TableCell>}
                  {visibleColumns.date && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.countDate}</TableCell>}
                  {visibleColumns.location && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.warehouseName}/{t.branchName}</TableCell>}
                  {visibleColumns.item && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.itemName}</TableCell>}
                  {visibleColumns.systemQty && <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.systemQty}</TableCell>}
                  {visibleColumns.physicalQty && <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.physicalQty}</TableCell>}
                  {visibleColumns.variance && <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.variance}</TableCell>}
                  {visibleColumns.value && <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.varianceValue}</TableCell>}
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.status}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredData().slice(detailPage * detailRowsPerPage, detailPage * detailRowsPerPage + detailRowsPerPage).map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'action.hover' }, backgroundColor: index % 2 === 0 ? 'background.default' : 'action.hover' }}>
                    {visibleColumns.countNumber && <TableCell>{row.countNumber}</TableCell>}
                    {visibleColumns.date && <TableCell>{formatDate(row.countDate)}</TableCell>}
                    {visibleColumns.location && <TableCell>{row.warehouseName || row.branchName}</TableCell>}
                    {visibleColumns.item && (
                      <TableCell>
                        <Typography variant="body2" fontWeight="500">{isRTL ? row.itemNameAr : row.itemName}</Typography>
                        <Typography variant="caption" color="text.secondary">{row.itemCode}</Typography>
                      </TableCell>
                    )}
                    {visibleColumns.systemQty && <TableCell align="right">{formatCurrency(row.systemQuantity)}</TableCell>}
                    {visibleColumns.physicalQty && <TableCell align="right">{formatCurrency(row.physicalQuantity || 0)}</TableCell>}
                    {visibleColumns.variance && (
                      <TableCell align="right">
                        <Typography sx={{ color: (row.variance || 0) >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                          {formatCurrency(row.variance || 0)}
                        </Typography>
                      </TableCell>
                    )}
                    {visibleColumns.value && (
                      <TableCell align="right">
                        <Typography sx={{ color: (row.varianceValue || 0) >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                          {formatCurrency(row.varianceValue || 0)}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip label={getStatusLabel(row.status)} size="small" color={getStatusColor(row.status)} />
                    </TableCell>
                  </TableRow>
                ))}
                {getFilteredData().length === 0 && (
                  <TableRow><TableCell colSpan={12} align="center" sx={{ py: 5 }}><Typography color="text.secondary">{t.noData}</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={getFilteredData().length}
              page={detailPage}
              onPageChange={(_, p) => setDetailPage(p)}
              rowsPerPage={detailRowsPerPage}
              onRowsPerPageChange={(e) => { setDetailRowsPerPage(parseInt(e.target.value, 10)); setDetailPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              className="no-print"
            />
            </>
          )}

          {currentTab === 1 && (
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.warehouseName}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.countsCount}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.itemsCounted}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalVarianceValue}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.positive}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.negative}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getSummaryByWarehouse().map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell sx={{ fontWeight: '500' }}>{isRTL ? row.locationNameAr : row.locationName}</TableCell>
                    <TableCell align="right">{row.countsCount}</TableCell>
                    <TableCell align="right">{row.totalItems}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: row.totalVarianceValue >= 0 ? 'success.main' : 'error.main' }}>
                      {formatCurrency(row.totalVarianceValue)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'success.main' }}>{formatCurrency(row.positiveVariance)}</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>{formatCurrency(row.negativeVariance)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {currentTab === 2 && (
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.branchName}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.countsCount}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.itemsCounted}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalVarianceValue}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.positive}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.negative}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getSummaryByBranch().map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell sx={{ fontWeight: '500' }}>{isRTL ? row.locationNameAr : row.locationName}</TableCell>
                    <TableCell align="right">{row.countsCount}</TableCell>
                    <TableCell align="right">{row.totalItems}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: row.totalVarianceValue >= 0 ? 'success.main' : 'error.main' }}>
                      {formatCurrency(row.totalVarianceValue)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'success.main' }}>{formatCurrency(row.positiveVariance)}</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>{formatCurrency(row.negativeVariance)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {currentTab === 3 && (
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.itemName}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.countsCount}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.systemQty}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.physicalQty}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.variance}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.varianceValue}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getSummaryByItem().map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="500">{isRTL ? row.itemNameAr : row.itemName}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.itemCode}</Typography>
                    </TableCell>
                    <TableCell align="right">{row.countsCount}</TableCell>
                    <TableCell align="right">{formatCurrency(row.totalSystemQty)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.totalPhysicalQty)}</TableCell>
                    <TableCell align="right" sx={{ color: row.totalVariance >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                      {formatCurrency(row.totalVariance)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: row.totalVarianceValue >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                      {formatCurrency(row.totalVarianceValue)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {currentTab === 4 && (
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #1565C0 30%, #1976D2 90%)' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.itemName}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.systemQty}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.physicalQty}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.variance}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.varianceValue}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.variancePct}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getVarianceAnalysis().map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="500">{isRTL ? row.itemNameAr : row.itemName}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.itemCode}</Typography>
                    </TableCell>
                    <TableCell align="right">{formatCurrency(row.systemQuantity)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.physicalQuantity)}</TableCell>
                    <TableCell align="right" sx={{ color: row.variance >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                      {formatCurrency(row.variance)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: row.varianceValue >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                      {formatCurrency(row.varianceValue)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: Math.abs(row.variancePct) > 10 ? 'error.main' : 'text.secondary', fontWeight: 'bold' }}>
                      {row.variancePct.toFixed(2)}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      )}

      <Menu anchorEl={columnsAnchor} open={Boolean(columnsAnchor)} onClose={() => setColumnsAnchor(null)}>
        {Object.keys(visibleColumns).map((key) => (
          <MenuItem key={key}>
            <FormControlLabel control={<Checkbox checked={visibleColumns[key as keyof typeof visibleColumns]}
              onChange={(e) => setVisibleColumns({ ...visibleColumns, [key]: e.target.checked })} />} label={key} />
          </MenuItem>
        ))}
      </Menu>

      <Box className="print-footer">
        <Typography sx={{ fontSize: '8px', color: '#546E7A' }}>
          {isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName} - {isRTL ? t.titleAr : t.title}
          <span style={{ marginLeft: '20px', marginRight: '20px' }}>|</span>
          <span className="page-number"></span>
        </Typography>
      </Box>

      <style>{`
        @media print {
          header, nav, .MuiAppBar-root, .MuiDrawer-root, .MuiToolbar-root { display: none !important; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          @page { margin: 0.5cm 1cm 1.5cm 1cm; size: landscape; }
          * { orphans: 3; widows: 3; }
          table { page-break-after: avoid; }
          body { print-color-adjust: exact; -webkit-print-color-adjust: exact; font-size: 9px; }
          main, .MuiBox-root { margin: 0 !important; padding: 8px !important; max-width: 100% !important; }
          table thead tr { background: linear-gradient(135deg, #1565C0 0%, #1976D2 100%) !important; -webkit-print-color-adjust: exact; }
          table thead th { background: linear-gradient(135deg, #1565C0 0%, #1976D2 100%) !important; color: white !important; font-weight: bold !important; padding: 4px 6px !important; border: 1px solid #1565C0 !important; font-size: 9px !important; -webkit-print-color-adjust: exact; }
          table tbody tr:nth-child(odd) { background-color: #F5F9FF !important; -webkit-print-color-adjust: exact; }
          table tbody tr:nth-child(even) { background-color: #FFFFFF !important; -webkit-print-color-adjust: exact; }
          table tbody td { padding: 3px 6px !important; border: 1px solid #E3F2FD !important; color: #263238 !important; font-size: 8px !important; line-height: 1.2 !important; -webkit-print-color-adjust: exact; }
          table tbody td .MuiTypography-caption { color: #546E7A !important; opacity: 1 !important; }
          .MuiChip-root { height: 16px !important; font-size: 7px !important; -webkit-print-color-adjust: exact; }
          .MuiChip-colorSuccess { background-color: #4CAF50 !important; color: white !important; -webkit-print-color-adjust: exact; }
          .MuiChip-colorInfo { background-color: #2196F3 !important; color: white !important; -webkit-print-color-adjust: exact; }
          .MuiChip-colorWarning { background-color: #FF9800 !important; color: white !important; -webkit-print-color-adjust: exact; }
          .MuiTableContainer-root { box-shadow: none !important; border-radius: 0 !important; overflow: visible !important; }
          .print-only h4, .print-only h3, .print-only h5 { color: #1565C0 !important; -webkit-print-color-adjust: exact; }
          .print-only .MuiPaper-root { -webkit-print-color-adjust: exact; box-shadow: none !important; }
          .print-only img { -webkit-print-color-adjust: exact; }
          .print-footer { position: fixed; bottom: 0; left: 0; right: 0; text-align: center; font-size: 8px; padding: 5px; border-top: 1px solid #1565C0; background: white; }
        }
        @media screen { .print-only { display: none !important; } .print-footer { display: none !important; } }
      `}</style>
    </Box>
  );
};

export default StockCountsReport;